import { useEffect, useState } from "react";
import { ExternalLink, Info, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { ZoomableImage } from "@/components/ZoomableImage";
import { RichText } from "@/components/RichText";

const hasMoreInfo = (s) => !!(s.more_info_text || (s.more_info_images || []).length > 0);

export default function Sponsors() {
  const [site, setSite] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [active]);

  const sponsors = site?.sponsors || [];

  return (
    <Layout>
      <Seo title="Billiards Partners & League Sponsors | Quinte BCA" description="Meet the local businesses and billiards partners that proudly sponsor and support the Quinte BCA Pool League in Trenton and the Quinte region." path="/partners" />
      <PageHeader eyebrow="Our partners" title="Partners" subtitle={site?.sponsors_intro} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {sponsors.length === 0 ? (
          <p className="text-[#555555]">Partner information is coming soon.</p>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="sponsors-grid">
            {sponsors.map((s) => {
              const showInfo = hasMoreInfo(s);
              return (
                <div
                  key={s.id}
                  data-testid={`sponsor-card-${s.id}`}
                  className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow"
                >
                  <div className="h-40 w-full flex items-center justify-center mb-4">
                    {s.image ? (
                      s.website ? (
                        <a href={s.website} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-logo-link-${s.id}`} className="block h-full w-full flex items-center justify-center hover:opacity-80 transition-opacity">
                          <img src={fileUrl(s.image)} alt={s.name} className="max-h-40 max-w-full object-contain" />
                        </a>
                      ) : showInfo ? (
                        <button onClick={() => setActive(s)} data-testid={`sponsor-logo-info-${s.id}`} className="block h-full w-full flex items-center justify-center hover:opacity-80 transition-opacity">
                          <img src={fileUrl(s.image)} alt={s.name} className="max-h-40 max-w-full object-contain" />
                        </button>
                      ) : (
                        <img src={fileUrl(s.image)} alt={s.name} className="max-h-40 max-w-full object-contain" />
                      )
                    ) : (
                      <div className="text-gray-300 text-sm">No logo</div>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-lg">{s.name}</h3>
                  {s.description && <p className="text-sm text-[#555555] mt-2 leading-relaxed">{s.description}</p>}
                  {(s.website || showInfo) && (
                    <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                      {showInfo && (
                        <button
                          onClick={() => setActive(s)}
                          data-testid={`sponsor-more-info-${s.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold text-sm hover:bg-[#174e9c] transition-colors"
                        >
                          <Info className="h-4 w-4" /> More info
                        </button>
                      )}
                      {s.website && (
                        <a
                          href={s.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sponsor-link-${s.id}`}
                          className="inline-flex items-center gap-1.5 text-[#1E63C4] font-semibold text-sm hover:gap-2.5 transition-all"
                        >
                          Visit website <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="sponsor-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                {active.image && <img src={fileUrl(active.image)} alt={active.name} className="h-10 w-10 object-contain" />}
                <h3 className="font-heading font-bold text-xl">{active.name}</h3>
              </div>
              <button onClick={() => setActive(null)} className="text-[#555555] hover:text-[#111111]" data-testid="sponsor-modal-close">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {active.more_info_text && <RichText html={active.more_info_text} testid="sponsor-moreinfo-view" />}
              {(active.more_info_images || []).length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {active.more_info_images.map((img, i) => (
                    <div key={i} className="overflow-hidden rounded-lg border border-[#E5E7EB]">
                      <ZoomableImage
                        src={fileUrl(img)}
                        alt={`${active.name} ${i + 1}`}
                        testId={`sponsor-modal-image-${i}`}
                        className="w-full h-auto"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
